/* eslint-disable max-lines */
import {
    Button, ButtonGroup, FormControl, FormErrorMessage,
    FormLabel, HStack, IconButton, Input, InputGroup, InputRightElement, Link, Menu, MenuButton,
    MenuItem,
    MenuItemOption, MenuList, ModalBody, ModalFooter, Spacer, Text, VStack,
} from '@chakra-ui/react';
import {
    Control, Controller, useFieldArray, useFormContext,
} from 'react-hook-form';
import { useState } from 'react';
import { ContactNumberType, getReadableContactNumberType } from '@acp/shared';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { FaIcon, ThemeTokens } from '@acp/core-ui';
import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { OccupantCard } from '../../../occupant/components/occupant-card';
import { TenancyStep } from '../tenancy-steps';
import { FormValues } from '../schemas';

function DefaultScreen({
    onOccupantAdd,
    onOccupantEdit,
    onOccupantRemove,
    onSetScreen,
}: {
    onOccupantAdd: () => void;
    onOccupantEdit: (index: number) => void;
    onOccupantRemove: (index: number) => void;
    onSetScreen: (screen: TenancyStep) => void;
}) {
    const form = useFormContext<FormValues>();

    const occupants = form.getValues('occupants');

    return (
        <>
            <ModalBody minH="lg" px={ThemeTokens.TenancyModal.body.py}>
                <VStack align="left" gap={6}>
                    {
                        occupants.length === 0 && (
                            <VStack>
                                <Text color="muted.500">
                                    There are no occupants assigned
                                    to this tenancy.
                                </Text>
                                <Button
                                    as={Link}
                                    variant="unstyled"
                                    fontWeight="normal"
                                    textDecor="underline"
                                    color="secondary.500"
                                    onClick={onOccupantAdd}
                                    cursor="pointer"
                                >
                                    Click here to add an occupant
                                </Button>
                            </VStack>
                        )
                    }
                    {
                        occupants.length > 0 && (
                            <VStack align="left" gap={4}>
                                {
                                    occupants.map((occupant, index) => (
                                        (
                                            <OccupantCard
                                                // eslint-disable-next-line react/no-array-index-key
                                                key={index}
                                                showActions
                                                {...occupant}
                                            >
                                                <ButtonGroup
                                                    size="sm"
                                                    isAttached
                                                    colorScheme="charcoal"
                                                    variant="outline"
                                                >
                                                    <Button
                                                        borderRightWidth={1}
                                                        borderRightColor="secondary.900"
                                                        color="muted.700"
                                                        borderColor={
                                                            ThemeTokens.Defaults.borderColor
                                                        }
                                                        onClick={() => onOccupantEdit(index)}
                                                    >
                                                        Edit
                                                    </Button>
                                                    <Menu size="large">
                                                        <MenuButton
                                                            as={IconButton}
                                                            borderStartRadius={0}
                                                            icon={<FaIcon icon={faChevronDown} />}
                                                            borderColor={
                                                                ThemeTokens.Defaults.borderColor
                                                            }
                                                            color="muted.700"
                                                        />
                                                        <MenuList>
                                                            <MenuItem
                                                                as="button"
                                                                color="danger.500"
                                                                onClick={
                                                                    () => onOccupantRemove(index)
                                                                }
                                                            >
                                                                Remove occupant
                                                            </MenuItem>
                                                        </MenuList>
                                                    </Menu>
                                                </ButtonGroup>
                                            </OccupantCard>
                                        )
                                    ))
                                }
                                <Button
                                    as={Text}
                                    onClick={onOccupantAdd}
                                    w="fit-content"
                                    variant="unstyled"
                                    fontWeight="normal"
                                    textDecor="underline"
                                    cursor="pointer"
                                >
                                    Add another occupant
                                </Button>
                            </VStack>
                        )
                    }
                </VStack>
            </ModalBody>
            <ModalFooter>
                <Button
                    size="md"
                    colorScheme="muted"
                    variant="ghost"
                    onClick={() => onSetScreen(TenancyStep.LeaseAgreement)}
                >
                    Back
                </Button>
                <Spacer />
                <Button
                    colorScheme="secondary"
                    variant="solid"
                    size="md"
                    onClick={() => onSetScreen(TenancyStep.Confirmation)}
                >
                    Next
                </Button>
            </ModalFooter>
        </>
    );
}

function OccupantContactsForm({
    occupantIndex,
    contactIndex,
    onRemoveContactNumber,
}: {
    occupantIndex: number;
    contactIndex: number;
    onRemoveContactNumber: (index: number) => void;
}) {
    const { register, control } = useFormContext<FormValues>();

    return (
        <VStack align="left" gap={0}>
            <HStack align="start" alignItems="stretch">
                <Controller
                    control={control}
                    name={`occupants.${occupantIndex}.contactNumbers.${contactIndex}.type`}
                    render={({ field, fieldState }) => (
                        <FormControl isInvalid={fieldState.invalid} w="fit-content">
                            <Menu {...field} size="sm">
                                <MenuButton
                                    w={100}
                                    as={Button}
                                    rightIcon={<ChevronDownIcon />}
                                    colorScheme="charcoal"
                                    variant="outline"
                                    fontWeight="normal"
                                    fontSize="sm"
                                    borderColor="muted.50"
                                    _firstLetter={{ textTransform: 'capitalize' }}
                                >
                                    {getReadableContactNumberType(field.value)}
                                </MenuButton>
                                <MenuList>
                                    <MenuItemOption
                                        value={ContactNumberType.Mobile}
                                        onClick={() => field.onChange(
                                            ContactNumberType.Mobile
                                        )}
                                    >
                                        Mobile
                                    </MenuItemOption>
                                    <MenuItemOption
                                        value={ContactNumberType.Home}
                                        onClick={() => field.onChange(
                                            ContactNumberType.Home
                                        )}
                                    >
                                        Home
                                    </MenuItemOption>
                                    <MenuItemOption
                                        value={ContactNumberType.Office}
                                        onClick={() => field.onChange(
                                            ContactNumberType.Office
                                        )}
                                    >
                                        Office
                                    </MenuItemOption>
                                    <MenuItemOption
                                        value={ContactNumberType.Other}
                                        onClick={() => field.onChange(
                                            ContactNumberType.Other
                                        )}
                                    >
                                        Other
                                    </MenuItemOption>
                                </MenuList>
                            </Menu>
                            {fieldState.error && (
                                <FormErrorMessage>{fieldState.error.message}</FormErrorMessage>
                            )}
                        </FormControl>
                    )}
                />
                <Controller
                    control={control}
                    name={`occupants.${occupantIndex}.contactNumbers.${contactIndex}.number`}
                    render={({ fieldState }) => (
                        <FormControl isInvalid={fieldState.invalid}>
                            <InputGroup>
                                <Input
                                    {...register(`occupants.${occupantIndex}.contactNumbers.${contactIndex}.number` as const)}
                                    placeholder="Contact number"
                                    fontSize="sm"
                                    type="string"
                                />
                                <InputRightElement>
                                    <Button
                                        onClick={() => onRemoveContactNumber(contactIndex)}
                                        size="xs"
                                        color="white"
                                        bgColor="muted.100"
                                        _hover={{ bgColor: 'red.500' }}
                                    >
                                        <FaIcon icon={faTrash} />
                                    </Button>
                                </InputRightElement>
                            </InputGroup>
                            {fieldState.error && (
                                <FormErrorMessage>{fieldState.error.message}</FormErrorMessage>
                            )}
                        </FormControl>
                    )}
                />
            </HStack>
        </VStack>
    );
}

function AddEditOccupantScreen({
    index,
    control,
    onOccupantSubmit,
    onPreviousScreen,
}: {
    index: number;
    control: Control<FormValues>;
    onOccupantSubmit: (data: FormValues) => void;
    onPreviousScreen: (index: number) => void;
}) {
    const { register, handleSubmit } = useFormContext<FormValues>();
    const { fields, append, remove } = useFieldArray({
        control,
        name: `occupants.${index}.contactNumbers`,
    });

    const handleAddContactNumber = () => {
        append({
            number: '',
            type: ContactNumberType.Mobile,
        });
    };

    const handleRemoveContactNumber = (_index: number) => {
        remove(_index);
    };

    return (
        <>
            <ModalBody minH="lg" px={ThemeTokens.TenancyModal.body.py}>
                <VStack align="left" spacing={6}>
                    <Controller
                        control={control}
                        name={`occupants.${index}.name`}
                        render={({ fieldState }) => (
                            <FormControl isInvalid={fieldState.invalid} isRequired>
                                <FormLabel htmlFor={`occupants.${index}.name`}>Name</FormLabel>
                                <Input
                                    {...register(`occupants.${index}.name` as const)}
                                    placeholder="Full name"
                                    fontSize="sm"
                                />
                                {fieldState.error && (
                                    <FormErrorMessage>{fieldState.error.message}</FormErrorMessage>
                                )}
                            </FormControl>
                        )}
                    />
                    <Controller
                        control={control}
                        name={`occupants.${index}.email`}
                        render={({ fieldState }) => (
                            <FormControl isInvalid={fieldState.invalid}>
                                <FormLabel htmlFor={`occupants.${index}.email`}>Email</FormLabel>
                                <Input
                                    {...register(`occupants.${index}.email` as const)}
                                    placeholder="Email address"
                                    fontSize="sm"
                                />
                                {fieldState.error && (
                                    <FormErrorMessage>{fieldState.error.message}</FormErrorMessage>
                                )}
                            </FormControl>
                        )}
                    />
                    <VStack align="left" spacing={2}>
                        <FormLabel htmlFor={`occupants.${index}.contactNumbers`} mb={0}>
                            Contact numbers
                        </FormLabel>
                        {
                            fields.map((field, contactIndex) => (
                                <OccupantContactsForm
                                    key={field.id}
                                    occupantIndex={index}
                                    contactIndex={contactIndex}
                                    onRemoveContactNumber={handleRemoveContactNumber}
                                />
                            ))
                        }
                        <Button
                            as={Text}
                            w="fit-content"
                            variant="unstyled"
                            fontWeight="normal"
                            textDecor="underline"
                            onClick={handleAddContactNumber}
                            cursor="pointer"
                        >
                            Add contact number
                        </Button>
                    </VStack>
                </VStack>
            </ModalBody>
            <ModalFooter>
                <Button
                    size="md"
                    colorScheme="muted"
                    variant="ghost"
                    onClick={() => onPreviousScreen(index)}
                >
                    Back
                </Button>
                <Spacer />
                <Button
                    colorScheme="secondary"
                    variant="solid"
                    size="md"
                    onClick={handleSubmit(onOccupantSubmit)}
                >
                    Save occupant
                </Button>
            </ModalFooter>
        </>
    );
}

export function OccupantsScreen({
    onSetScreen,
}: {
    onSetScreen: (step: TenancyStep) => void;
}) {
    const [screen, setScreen] = useState<'DEFAULT' | 'ADD_OCCUPANT' | 'EDIT_OCCUPANT'>('DEFAULT');
    const [editOccupantIndex, setEditOccupantIndex] = useState<number>(0);
    const { control, resetField } = useFormContext<FormValues>();
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'occupants',
    });

    const handleOccupantAdd = () => {
        append({
            name: '',
            email: '',
            contactNumbers: [
                {
                    number: '',
                    type: ContactNumberType.Mobile,
                },
            ],
        });
        setScreen('ADD_OCCUPANT');
    };

    const handlePreviousScreen = (index: number) => {
        remove(index);
        setScreen('DEFAULT');
    };

    const handleOccupantEdit = (index: number) => {
        setEditOccupantIndex(index);
        setScreen('EDIT_OCCUPANT');
    };

    const handleOccupantRemove = (index: number) => {
        remove(index);
    };

    const handlePreviousScreenEdit = () => {
        resetField(`occupants.${editOccupantIndex}`);
        setScreen('DEFAULT');
    };

    const handleAddOccupantSubmit = () => {
        setScreen('DEFAULT');
    };

    return (
        <>
            {
                screen === 'DEFAULT' && (
                    <DefaultScreen
                        onOccupantAdd={handleOccupantAdd}
                        onOccupantEdit={handleOccupantEdit}
                        onOccupantRemove={handleOccupantRemove}
                        onSetScreen={onSetScreen}
                    />
                )
            }
            {
                screen === 'ADD_OCCUPANT' && (
                    <AddEditOccupantScreen
                        index={fields.length - 1}
                        control={control}
                        onOccupantSubmit={handleAddOccupantSubmit}
                        onPreviousScreen={handlePreviousScreen}
                    />
                )
            }
            {
                screen === 'EDIT_OCCUPANT' && (
                    <AddEditOccupantScreen
                        index={editOccupantIndex}
                        control={control}
                        onOccupantSubmit={handleAddOccupantSubmit}
                        onPreviousScreen={handlePreviousScreenEdit}
                    />
                )
            }
        </>
    );
}
